import { useUser } from "@clerk/clerk-react";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  ArrowLeft,
  Bot,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Download,
  FileText,
  Loader2,
  MoreVertical,
  RefreshCw,
  Save,
} from "lucide-react";
import { useEffect, useState } from "react";
import { type Control, useFieldArray, useForm, useWatch } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { z } from "zod";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { InlineCitation } from "@/components/ui/inline-citation";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  useCommitAdminReview,
  usePromoteReviewToFixture,
  useRetryReview,
  useReview,
  useReviewAudit,
  useReviewBySubmission,
  useReviewTopic,
  useSubmission,
  useSubmissionFileUrl,
} from "@/hooks/admin/reviews";
import { useUserProfile } from "@/hooks/useUserProfile";
import { AdminLayout } from "@/layouts/AdminLayout";
import { isAdminRole, isSuperadminRole } from "@/lib/roles";
import type { Review } from "@/types";

const SECTION_KEYS = [
  "research_integrity",
  "analytical_reasoning",
  "relevance_and_investment_impact",
  "clarity_and_communication",
  "professional_and_ethical_standards",
];

const SCORING_GUIDE: Record<
  string,
  {
    maxScore: number;
    levels: { name: string; range: string }[];
    interpretation: { range: string; description: string }[];
    caps: string[];
  }
> = {
  analytical_reasoning: {
    maxScore: 25,
    levels: [
      { name: "Foundation", range: "0–9" },
      { name: "Intermediate", range: "10–15" },
      { name: "Advanced", range: "16–21" },
      { name: "Professional", range: "22–25" },
    ],
    interpretation: [
      { range: "0–9", description: "Descriptive, no analytical structure" },
      { range: "10–15", description: "Basic competence — drivers and causal links present" },
      { range: "16–21", description: "Strong reasoning — evidence weighed and assumptions tested" },
      {
        range: "22–25",
        description: "Institution-grade — scenario logic and calibrated confidence",
      },
    ],
    caps: [
      "A1 or A2 absent → max 9/25",
      "A3 absent → max 15/25",
      "A4 absent → max 20/25",
      "A7 absent → max 22/25",
    ],
  },
  research_integrity: {
    maxScore: 15,
    levels: [
      { name: "Foundation", range: "0–5" },
      { name: "Intermediate", range: "6–9" },
      { name: "Advanced", range: "10–12" },
      { name: "Professional", range: "13–15" },
    ],
    interpretation: [
      { range: "0–5", description: "Weak attribution and poor source credibility" },
      { range: "6–9", description: "Generally relevant sources; limited quality evaluation" },
      { range: "10–12", description: "Strong attribution and quality assessment" },
      {
        range: "13–15",
        description: "Institution-grade — transparent on limitations and conflicts",
      },
    ],
    caps: ["B1 or B3 absent → max 5/15", "B4 absent → max 9/15", "B2 absent → max 12/15"],
  },
  relevance_and_investment_impact: {
    maxScore: 25,
    levels: [
      { name: "Foundation", range: "0–9" },
      { name: "Intermediate", range: "10–15" },
      { name: "Advanced", range: "16–21" },
      { name: "Professional", range: "22–25" },
    ],
    interpretation: [
      { range: "0–9", description: "Topic touched but no clear investment link" },
      { range: "10–15", description: "Relevant with basic investment implications" },
      { range: "16–21", description: "Strong relevance — clear actionable implications" },
      { range: "22–25", description: "Institution-grade — conditional thesis with scenarios" },
    ],
    caps: [
      "C1 or C2 absent → max 9/25",
      "C3 absent → max 15/25",
      "C5 absent → max 20/25",
      "C6 absent → max 22/25",
    ],
  },
  clarity_and_communication: {
    maxScore: 15,
    levels: [
      { name: "Foundation", range: "0–5" },
      { name: "Intermediate", range: "6–9" },
      { name: "Advanced", range: "10–12" },
      { name: "Professional", range: "13–15" },
    ],
    interpretation: [
      { range: "0–5", description: "Poorly structured, unclear communication" },
      { range: "6–9", description: "Logical structure; some clarity issues" },
      { range: "10–12", description: "Well-structured with clear progression" },
      { range: "13–15", description: "Publication-ready — precise and cohesive throughout" },
    ],
    caps: [
      "D2 absent → max 5/15",
      "D3 absent → max 9/15",
      "D4 absent → max 12/15",
      "D6 absent → max 13/15 (soft)",
    ],
  },
  professional_and_ethical_standards: {
    maxScore: 20,
    levels: [
      { name: "Foundation", range: "0–8" },
      { name: "Intermediate", range: "9–12" },
      { name: "Advanced", range: "13–16" },
      { name: "Professional", range: "17–20" },
    ],
    interpretation: [
      { range: "0–8", description: "Significant ethical or formatting gaps" },
      { range: "9–12", description: "Basic compliance with some gaps" },
      { range: "13–16", description: "Strong standards — ethical awareness evident" },
      { range: "17–20", description: "Institution-grade — full ethical and professional rigour" },
    ],
    caps: [
      "E1 absent → max 8/20",
      "E2 absent → max 12/20",
      "E4 absent → max 16/20",
      "E6 absent → max 18/20",
    ],
  },
};

const formatSectionName = (key: string) => {
  return key
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ")
    .replace(" And ", " & ");
};

interface EditableSection {
  score: number;
  max_score: number;
  band?: string;
  observable_features_detected: string[];
  missing_features_for_next_band: string[];
  diagnostic_explanation: string;
  // Assessor-only reasoning for the score (KC-444). Never rendered on
  // participant surfaces — those read named fields and skip this one.
  assessor_note: string;
}

// react-hook-form requires object items for field arrays, so the two string
// lists are modelled as { value } rows. `max_score`/`band` are carried (not
// directly edited) so scoring clamps and the saved payload stay identical.
const listItemSchema = z.object({ value: z.string() });
const editableSectionSchema = z.object({
  score: z.number().int().min(0),
  max_score: z.number(),
  band: z.string().optional(),
  observable_features_detected: z.array(listItemSchema),
  missing_features_for_next_band: z.array(listItemSchema),
  diagnostic_explanation: z.string(),
  assessor_note: z.string(),
});

const QUALITY_VALUES = ["low", "med", "high"] as const;
const FLAG_VALUES = ["hallucination", "bias", "score_error", "other"] as const;
type QualityValue = (typeof QUALITY_VALUES)[number];
type FlagValue = (typeof FLAG_VALUES)[number];

const FLAG_LABELS: Record<FlagValue, string> = {
  hallucination: "Hallucination",
  bias: "Bias",
  score_error: "Score error",
  other: "Other",
};

const adminFeedbackSchema = z.object({
  quality: z.enum(QUALITY_VALUES).nullable(),
  flags: z.array(z.enum(FLAG_VALUES)),
  note: z.string(),
});

const reviewFormSchema = z.object({
  sections: z.record(z.string(), editableSectionSchema),
  adminFeedback: adminFeedbackSchema,
});
type ReviewFormValues = z.infer<typeof reviewFormSchema>;
type FormSection = z.infer<typeof editableSectionSchema>;

type ListField = "observable_features_detected" | "missing_features_for_next_band";

function toFormSections(editable: Record<string, EditableSection>): Record<string, FormSection> {
  const out: Record<string, FormSection> = {};
  for (const [key, s] of Object.entries(editable)) {
    out[key] = {
      score: s.score,
      max_score: s.max_score,
      band: s.band,
      observable_features_detected: s.observable_features_detected.map((value) => ({ value })),
      missing_features_for_next_band: s.missing_features_for_next_band.map((value) => ({ value })),
      diagnostic_explanation: s.diagnostic_explanation,
      assessor_note: s.assessor_note,
    };
  }
  return out;
}

const LIST_FIELD_CONFIG: Record<
  ListField,
  { label: string; border: string; text: string; btn: string }
> = {
  observable_features_detected: {
    label: "Observable Features",
    border: "border-primary",
    text: "text-green-700",
    btn: "text-green-600 hover:text-green-700",
  },
  missing_features_for_next_band: {
    label: "To Reach Next Band",
    border: "border-amber-500",
    text: "text-amber-700",
    btn: "text-amber-600 hover:text-amber-700",
  },
};

// One dynamic string list (observable / missing) for a single section, backed
// by react-hook-form's useFieldArray so add/remove/edit stay form-managed.
function SectionListField({
  control,
  sectionKey,
  field,
  disabled,
  canEdit,
}: {
  control: Control<ReviewFormValues>;
  sectionKey: string;
  field: ListField;
  disabled: boolean;
  canEdit: boolean;
}) {
  const config = LIST_FIELD_CONFIG[field];
  const { fields, append, remove } = useFieldArray({
    control,
    name: `sections.${sectionKey}.${field}`,
  });

  return (
    <div>
      <h5 className={`text-sm font-semibold ${config.text} mb-1.5`}>{config.label}</h5>
      <div className="space-y-2">
        {fields.map((item, idx) => (
          <div key={item.id} className="flex items-start gap-2">
            <div className={`flex-1 border-l-2 ${config.border} pl-3`}>
              <FormField
                control={control}
                name={`sections.${sectionKey}.${field}.${idx}.value`}
                render={({ field: f }) => (
                  <FormItem className="space-y-0">
                    <FormControl>
                      <Textarea
                        {...f}
                        value={String(f.value ?? "")}
                        disabled={disabled}
                        rows={1}
                        style={{ fieldSizing: "content" } as React.CSSProperties}
                        className="w-full bg-white border-gray-200 rounded px-3 py-2 text-sm text-black focus:border-gray-400 focus:ring-0 resize-none overflow-hidden min-h-0"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {canEdit && (
              <button
                type="button"
                onClick={() => remove(idx)}
                className="text-gray-400 hover:text-red-500 text-xs mt-2 transition-colors"
              >
                Remove
              </button>
            )}
          </div>
        ))}
        {canEdit && (
          <button
            type="button"
            onClick={() => append({ value: "" })}
            className={`text-sm font-medium ${config.btn} transition-colors`}
          >
            + Add item
          </button>
        )}
      </div>
    </div>
  );
}

type ReviewVersionFields = Pick<Review, "rubric_version" | "prompt_version">;

/** "Rubric v1 · Prompt legacy-12"; null for legacy reviews that recorded neither. */
function formatReviewVersions(review: ReviewVersionFields): string | null {
  const parts = [
    review.rubric_version && `Rubric v${review.rubric_version}`,
    review.prompt_version && `Prompt ${review.prompt_version}`,
  ].filter(Boolean);
  return parts.length ? parts.join(" · ") : null;
}

function ReviewVersions({ review }: { review: ReviewVersionFields }) {
  const label = formatReviewVersions(review);
  if (!label) return null;
  return <span className="text-xs text-gray-400">{label}</span>;
}

function formatAuditValue(value: unknown): string {
  if (value == null) return "—";
  if (Array.isArray(value)) return value.join(", ") || "—";
  return String(value);
}

/** Approval audit trail: who committed the final review, when, and what they changed vs the AI draft. */
function ApprovalHistory({ reviewId }: { reviewId: string }) {
  const { data: entries } = useReviewAudit(reviewId);
  if (!entries?.length) return null;

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-2">
        Approval history
      </div>
      <div className="space-y-3">
        {entries.map((entry) => {
          const sectionChanges = Object.entries(entry.changes.sections ?? {});
          const hasChanges = Boolean(entry.changes.final_score) || sectionChanges.length > 0;
          return (
            <div key={entry.id} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between text-xs">
                <span className="font-semibold text-gray-900">{entry.reviewer_name}</span>
                <span className="text-gray-500">{new Date(entry.created_at).toLocaleString()}</span>
              </div>
              {hasChanges ? (
                <div className="mt-2 space-y-1.5 text-xs text-gray-600">
                  {entry.changes.final_score && (
                    <div>
                      <span className="font-medium text-gray-700">Final score:</span>{" "}
                      <span className="line-through text-gray-400">
                        {entry.changes.final_score.ai}
                      </span>{" "}
                      → <span className="text-gray-900">{entry.changes.final_score.admin}</span>
                    </div>
                  )}
                  {sectionChanges.map(([section, fields]) => (
                    <div key={section}>
                      <span className="font-medium text-gray-700 capitalize">
                        {section.replace(/_/g, " ")}
                      </span>
                      <ul className="ml-4 mt-0.5 space-y-0.5">
                        {Object.entries(fields).map(([field, diff]) => (
                          <li key={field}>
                            <span className="text-gray-500">{field.replace(/_/g, " ")}:</span>{" "}
                            <span className="line-through text-gray-400 break-words">
                              {formatAuditValue(diff.ai)}
                            </span>{" "}
                            →{" "}
                            <span className="text-gray-900 break-words">
                              {formatAuditValue(diff.admin)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-1 text-xs text-gray-500">Approved AI review as-is</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function AdminReviewDetailPage() {
  const { reviewId } = useParams<{ reviewId: string }>();
  const navigate = useNavigate();
  const { userProfile } = useUserProfile();
  const { user } = useUser();
  // Assessors can grade but not run admin-only AI ops (retry / promote-to-fixture).
  // Superadmin additionally gets the always-available "Re-run AI review" action.
  const role = user?.publicMetadata?.role as string | undefined;
  const isAdmin = isAdminRole(role);
  const isSuperadmin = isSuperadminRole(role);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(SECTION_KEYS));
  const [showGuide, setShowGuide] = useState<Record<string, boolean>>({});
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Which revision is actively selected (affects both PDF viewer and review panel)
  const [activeRevision, setActiveRevision] = useState<1 | 2>(2);

  // ----------------------------------------------------------------
  // Data: review + submission + topic + history, via React Query.
  // The review detail query is keyed on `reviewId` (route param), so navigating
  // between reviews refetches naturally — no manual reload effect. The review
  // query also polls itself while `status === "pending"`. The submission, topic,
  // previous-submission, previous-review and PDF-url queries chain off ids and
  // are gated with `enabled`, replacing the old sequential `loadData`.
  // ----------------------------------------------------------------
  const reviewQuery = useReview(reviewId);
  const review = reviewQuery.data ?? null;

  const submissionQuery = useSubmission(review?.submission_id);
  const submission = submissionQuery.data ?? null;

  const topicQuery = useReviewTopic(submission?.topic_id);
  const topic = topicQuery.data ?? null;

  const previousSubmissionQuery = useSubmission(submission?.previous_submission_id ?? undefined);
  const previousSubmission = previousSubmissionQuery.data ?? null;

  const previousReviewQuery = useReviewBySubmission(previousSubmission?.id);
  const previousReview = previousReviewQuery.data ?? null;

  const currentFileUrlQuery = useSubmissionFileUrl(submission?.id);
  const previousFileUrlQuery = useSubmissionFileUrl(previousSubmission?.id);
  const pdfUrls: Record<string, string> = {};
  if (submission?.id && currentFileUrlQuery.data) pdfUrls[submission.id] = currentFileUrlQuery.data;
  if (previousSubmission?.id && previousFileUrlQuery.data) {
    pdfUrls[previousSubmission.id] = previousFileUrlQuery.data;
  }

  // The page's blocking spinner waits on the two core resources; everything
  // else (topic, history, PDF urls) degrades gracefully like before.
  const isLoading = reviewQuery.isPending || (Boolean(review) && submissionQuery.isPending);

  // Promote-to-fixture modal state
  const [promoteOpen, setPromoteOpen] = useState(false);
  const [promoteName, setPromoteName] = useState("");
  const [promoteAckNoAdmin, setPromoteAckNoAdmin] = useState(false);
  const promoteToFixture = usePromoteReviewToFixture();
  const promoting = promoteToFixture.isPending;
  const { mutateAsync: promoteToFixtureAsync } = promoteToFixture;
  const [promoteResult, setPromoteResult] = useState<
    | { kind: "success"; fixtureId: string }
    | { kind: "duplicate"; fixtureId: string }
    | { kind: "error"; message: string }
    | null
  >(null);

  const form = useForm<ReviewFormValues>({
    resolver: zodResolver(reviewFormSchema),
    defaultValues: { sections: {}, adminFeedback: { quality: null, flags: [], note: "" } },
  });
  const { control } = form;

  // Live total: sum of every section's current score (mirrors the old reduce).
  const watchedSections = useWatch({ control, name: "sections" });
  const totalScore = Object.values(watchedSections ?? {}).reduce(
    (sum, s) => sum + (s?.score ?? 0),
    0
  );
  const watchedQuality = useWatch({ control, name: "adminFeedback.quality" });

  // Seed the editable form from the loaded review whenever it changes (admin
  // sections take precedence over AI sections). Replaces the manual reset inside
  // the old `loadData`. React Query invalidation refetches the review after a
  // commit, which re-runs this and re-syncs the form.
  useEffect(() => {
    if (!review) return;
    const sourceSections = review.admin_sections || review.sections || {};
    const sections: Record<string, EditableSection> = {};
    for (const key of SECTION_KEYS) {
      const source = sourceSections[key];
      if (source) {
        sections[key] = {
          score: source.score || 0,
          max_score: source.max_score || 20,
          band: source.band || source.command_word_level,
          observable_features_detected: [
            ...(source.observable_features_detected || source.detected_behaviours || []),
          ],
          missing_features_for_next_band: [
            ...(source.missing_features_for_next_band || source.missing_behaviours || []),
          ],
          diagnostic_explanation: source.diagnostic_explanation || source.short_rationale || "",
          assessor_note: source.assessor_note || "",
        };
      } else {
        sections[key] = {
          score: 0,
          max_score: 20,
          observable_features_detected: [],
          missing_features_for_next_band: [],
          diagnostic_explanation: "",
          assessor_note: "",
        };
      }
    }
    const existingFeedback = sourceSections._admin_feedback as
      | { quality?: string; flags?: string[]; note?: string }
      | undefined;
    const adminFeedback = {
      quality: QUALITY_VALUES.includes(existingFeedback?.quality as QualityValue)
        ? (existingFeedback!.quality as QualityValue)
        : null,
      flags: (existingFeedback?.flags ?? []).filter((f): f is FlagValue =>
        FLAG_VALUES.includes(f as FlagValue)
      ),
      note: existingFeedback?.note ?? "",
    };
    form.reset({ sections: toFormSections(sections), adminFeedback });
  }, [review, form]);

  // Default to the current (latest) revision once the submission loads.
  useEffect(() => {
    if (submission) setActiveRevision(submission.revision_number > 1 ? 2 : 1);
  }, [submission]);

  const retryReview = useRetryReview();
  const isRetrying = retryReview.isPending;
  const isPolling = review?.status === "pending";

  const handleRetry = () => {
    if (!review) return;
    // The retry mutation flips the server status back to pending; invalidation
    // refetches the review, and `useReview` resumes polling while it's pending.
    retryReview.mutate(review.id, {
      onError: (error) => console.error("Failed to retry review:", error),
    });
  };

  // Superadmin-only: re-run the AI review from any status. Confirmed via a
  // dialog because it discards the current AI result and any admin grading.
  const [rerunOpen, setRerunOpen] = useState(false);
  const [rerunError, setRerunError] = useState<string | null>(null);

  const handleRerunConfirm = () => {
    if (!review) return;
    setRerunError(null);
    retryReview.mutate(review.id, {
      onSuccess: () => setRerunOpen(false),
      onError: (error) => setRerunError(error.message || "Failed to re-run AI review"),
    });
  };

  const toggleSection = (key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Clamp a typed score to [0, max_score], preserving the old updateScore rule.
  const updateScore = (key: string, score: number) => {
    const maxScore = form.getValues(`sections.${key}.max_score`) || 20;
    form.setValue(`sections.${key}.score`, Math.max(0, Math.min(maxScore, score)), {
      shouldDirty: true,
    });
  };

  const commitReview = useCommitAdminReview();
  const isSaving = commitReview.isPending;

  const { mutateAsync: commitReviewAsync } = commitReview;
  const handleCommitReview = form.handleSubmit(async (values) => {
    if (!review || !userProfile) return;

    setSaveSuccess(false);

    try {
      const adminSections: Record<string, any> = {};
      let computedTotal = 0;
      for (const [key, section] of Object.entries(values.sections)) {
        computedTotal += section.score;
        adminSections[key] = {
          score: section.score,
          band: section.band,
          observable_features_detected: section.observable_features_detected
            .map((i) => i.value)
            .filter((s) => s.trim()),
          missing_features_for_next_band: section.missing_features_for_next_band
            .map((i) => i.value)
            .filter((s) => s.trim()),
          diagnostic_explanation: section.diagnostic_explanation,
          assessor_note: section.assessor_note,
        };
      }

      adminSections._admin_feedback = {
        quality: values.adminFeedback.quality,
        flags: values.adminFeedback.flags,
        note: values.adminFeedback.note,
      };

      // Invalidation refetches the review, which re-syncs the form via the
      // section-seeding effect above (mirrors the old `await loadData()`).
      await commitReviewAsync({
        reviewId: review.id,
        data: {
          admin_sections: adminSections,
          admin_final_score: computedTotal,
        },
      });

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Failed to commit review:", error);
      alert("Failed to save review. Please try again.");
    }
  });

  const getScoreColor = (score: number, maxScore: number = 20) => {
    const pct = score / maxScore;
    if (pct >= 0.8) return "text-green-600";
    if (pct >= 0.6) return "text-amber-600";
    return "text-red-600";
  };

  const isEditable = review?.status === "submitted_for_review";
  const isAlreadyCommitted =
    review?.status === "admin_confirmed" || review?.status === "admin_updated";

  const hasHistory = !!(previousSubmission && previousReview);

  // Determine what to show based on active revision
  const activeSubmission =
    activeRevision === 1 && previousSubmission ? previousSubmission : submission;
  const activeReviewData = activeRevision === 1 && previousReview ? previousReview : review;
  const isViewingOriginal = activeRevision === 1 && hasHistory;

  // Promotion targets whichever revision is currently in view (Rev 1 = previous,
  // Rev 2 = current). Each revision has its own Review with its own scores.
  const promotionTarget = activeReviewData;
  const promotionRevision = activeRevision;
  const isReviewPromotable =
    promotionTarget?.status === "ai_reviewed" ||
    promotionTarget?.status === "admin_confirmed" ||
    promotionTarget?.status === "admin_updated";
  const defaultFixtureName = promotionTarget?.id
    ? `review-${promotionTarget.id.slice(0, 8)}-rev${promotionRevision}`
    : "review-fixture";

  const handlePromoteSubmit = async () => {
    if (!promotionTarget?.id) return;
    setPromoteResult(null);
    try {
      // If the review has no admin score, the user must have ticked the
      // acknowledgment — that overrides the server's default safety gate.
      const reviewLacksAdminScore =
        promotionTarget.admin_final_score === null ||
        promotionTarget.admin_final_score === undefined;
      const fixture = await promoteToFixtureAsync({
        reviewId: promotionTarget.id,
        name: promoteName.trim() || null,
        require_admin_score: !reviewLacksAdminScore,
      });
      setPromoteResult({ kind: "success", fixtureId: fixture.id });
    } catch (err) {
      const e = err as Error & { status?: number; existingFixtureId?: string };
      if (e.status === 409 && e.existingFixtureId) {
        setPromoteResult({ kind: "duplicate", fixtureId: e.existingFixtureId });
      } else {
        setPromoteResult({ kind: "error", message: e.message || "Promotion failed" });
      }
    }
  };

  // ================================================================
  // PDF VIEWER PANEL (left)
  // ================================================================
  const renderPdfPanel = () => {
    if (!activeSubmission) return null;
    const pdfUrl = pdfUrls[activeSubmission.id];
    const fileName =
      activeSubmission.original_filename ??
      activeSubmission.file_path.split("/").pop() ??
      "document.pdf";

    return (
      <div className="bg-white border border-gray-200 shadow-sm rounded-xl flex flex-col h-full overflow-hidden">
        <div className="px-4 py-2.5 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/reviews")}
              className="text-gray-400 hover:text-gray-700 transition-colors"
              aria-label="Back to Review Queue"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <FileText className="w-4 h-4 text-gray-400" />
            <span className="text-sm font-medium text-gray-900">
              Review #{review?.id?.slice(0, 8)}
            </span>
          </div>
          {hasHistory && (
            <div className="flex gap-1">
              <button
                onClick={() => setActiveRevision(1)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeRevision === 1
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Rev 1
              </button>
              <button
                onClick={() => setActiveRevision(2)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                  activeRevision === 2
                    ? "bg-gray-100 text-gray-900"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                Rev 2 (Current)
              </button>
            </div>
          )}
        </div>

        {/* PDF iframe */}
        <div className="flex-1 min-h-0">
          {pdfUrl ? (
            <iframe
              src={`${pdfUrl}#toolbar=1&navpanes=0`}
              className="w-full h-full border-0"
              title={`PDF Viewer - ${fileName}`}
            />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400 text-sm">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Loading document...
            </div>
          )}
        </div>
      </div>
    );
  };

  // ================================================================
  // READ-ONLY REVIEW PANEL (for revision 1)
  // ================================================================
  const renderReadOnlyPanel = () => {
    if (!activeReviewData) return null;
    const sections = activeReviewData.sections || {};
    const meta = sections._meta as any;
    const aiScore = SECTION_KEYS.reduce((sum, key) => sum + (sections[key]?.score || 0), 0);

    return (
      <div className="bg-white border border-gray-200 rounded-xl flex flex-col h-full overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-2">
            <Bot className="w-4 h-4 text-blue-600" />
            <span className="text-base font-semibold text-gray-900">AI Review</span>
            <ReviewVersions review={activeReviewData} />
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded font-medium">
              Read-only
            </span>
            {isAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    className="p-1 rounded hover:bg-gray-100 text-gray-500"
                    aria-label="Review actions"
                  >
                    <MoreVertical className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    disabled={!isReviewPromotable}
                    onSelect={(e) => {
                      e.preventDefault();
                      if (!isReviewPromotable) return;
                      setPromoteName(defaultFixtureName);
                      setPromoteAckNoAdmin(false);
                      setPromoteResult(null);
                      setPromoteOpen(true);
                    }}
                    title={
                      isReviewPromotable
                        ? undefined
                        : "Requires status ai_reviewed, admin_confirmed, or admin_updated"
                    }
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Promote to fixture
                  </DropdownMenuItem>
                  {isSuperadmin && (
                    <DropdownMenuItem
                      onSelect={(e) => {
                        e.preventDefault();
                        setRerunError(null);
                        setRerunOpen(true);
                      }}
                    >
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Re-run AI review
                    </DropdownMenuItem>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {/* Score */}
          <div className="text-center bg-gray-50 border border-gray-200 rounded-lg py-5 mb-5">
            <div className="text-xs text-gray-500 uppercase tracking-widest mb-1 font-medium">
              AI Score
            </div>
            <div className="text-5xl font-bold text-gray-900">{aiScore}</div>
            <div className="text-gray-400 text-base">/100</div>
          </div>

          {/* Relevance warning */}
          {meta && !meta.is_relevant && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
              <p className="text-amber-800 text-sm">
                <strong>Not Relevant</strong> ({meta.relevance_score}/100) —{" "}
                {meta.relevance_explanation}
              </p>
            </div>
          )}

          {/* Section scores */}
          <div className="space-y-3 mb-6">
            {SECTION_KEYS.map((key) => {
              const section = sections[key];
              if (!section) return null;
              return (
                <div key={key} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-700">
                      {formatSectionName(key)}
                    </span>
                    <div className="flex items-center gap-2">
                      {(section.band || section.command_word_level) && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-200 text-gray-500">
                          {section.band || section.command_word_level}
                        </span>
                      )}
                      <span
                        className={`text-sm font-semibold ${getScoreColor(section.score, section.max_score || 20)}`}
                      >
                        {section.score}/{section.max_score || 20}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Detailed feedback */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-[10px] text-gray-400 uppercase tracking-widest mb-3">
              Detailed Feedback
            </h4>
            {SECTION_KEYS.map((key) => {
              const section = sections[key];
              if (!section) return null;
              const features =
                section.observable_features_detected || section.detected_behaviours || [];
              const missing =
                section.missing_features_for_next_band || section.missing_behaviours || [];
              const diagnostic = section.diagnostic_explanation || section.short_rationale;
              if (!features.length && !missing.length && !diagnostic) return null;

              return (
                <div key={key} className="mb-4">
                  <h5 className="text-xs text-gray-600 font-medium mb-2">
                    {formatSectionName(key)}
                  </h5>
                  {diagnostic && (
                    <p className="text-xs text-gray-500 italic mb-2">
                      <InlineCitation text={diagnostic} />
                    </p>
                  )}
                  {features.length > 0 && (
                    <div className="mb-2">
                      <span className="text-[11px] text-green-700 font-medium">
                        Observable Features
                      </span>
                      {features.map((s: string, i: number) => (
                        <p
                          key={i}
                          className="text-xs text-gray-500 pl-2.5 border-l-2 border-green-300 mt-1"
                        >
                          <InlineCitation text={s} />
                        </p>
                      ))}
                    </div>
                  )}
                  {missing.length > 0 && (
                    <div className="mb-2">
                      <span className="text-[11px] text-amber-700 font-medium">
                        To Reach Next Band
                      </span>
                      {missing.map((s: string, i: number) => (
                        <p
                          key={i}
                          className="text-xs text-gray-500 pl-2.5 border-l-2 border-amber-300 mt-1"
                        >
                          <InlineCitation text={s} />
                        </p>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  // ================================================================
  // EDITABLE REVIEW PANEL (for current/latest revision)
  // ================================================================
  const renderEditablePanel = () => {
    if (!review) return null;
    const aiSections = review.sections || {};
    const meta = aiSections._meta as any;

    return (
      <Form {...form}>
        <form
          onSubmit={handleCommitReview}
          className="bg-white border border-gray-200 rounded-xl flex flex-col h-full overflow-hidden"
        >
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
            <div className="flex items-center gap-2">
              <Save className="w-4 h-4 text-green-600" />
              <span className="text-base font-semibold text-gray-900">Admin Review</span>
              <ReviewVersions review={review} />
            </div>
            <div className="flex items-center gap-2">
              <TooltipProvider delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    {isEditable ? (
                      <span className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded font-medium cursor-default">
                        Editable
                      </span>
                    ) : isAlreadyCommitted ? (
                      <span className="text-xs bg-green-50 text-green-700 px-2.5 py-1 rounded font-medium cursor-default">
                        Editable
                      </span>
                    ) : (
                      <span className="text-xs bg-gray-100 text-gray-500 px-2.5 py-1 rounded font-medium cursor-default">
                        Read-only
                      </span>
                    )}
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>
                      {isEditable
                        ? "Student has submitted for human review. You can edit scores and commit."
                        : isAlreadyCommitted
                          ? "This review has been committed. Editing will update the final grade."
                          : "Waiting for student to submit for human review."}
                    </p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              {isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      className="p-1 rounded hover:bg-gray-100 text-gray-500"
                      aria-label="Review actions"
                    >
                      <MoreVertical className="w-4 h-4" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem
                      disabled={!isReviewPromotable}
                      onSelect={(e) => {
                        e.preventDefault();
                        if (!isReviewPromotable) return;
                        setPromoteName(defaultFixtureName);
                        setPromoteAckNoAdmin(false);
                        setPromoteResult(null);
                        setPromoteOpen(true);
                      }}
                      title={
                        isReviewPromotable
                          ? undefined
                          : "Requires status ai_reviewed, admin_confirmed, or admin_updated"
                      }
                    >
                      <Download className="w-4 h-4 mr-2" />
                      Promote to fixture
                    </DropdownMenuItem>
                    {isSuperadmin && (
                      <DropdownMenuItem
                        onSelect={(e) => {
                          e.preventDefault();
                          setRerunError(null);
                          setRerunOpen(true);
                        }}
                      >
                        <RefreshCw className="w-4 h-4 mr-2" />
                        Re-run AI review
                      </DropdownMenuItem>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </div>

          {/* Scrollable body */}
          <div className="flex-1 overflow-y-auto p-5">
            {/* Pending / polling banner */}
            {(review?.status === "pending" || isPolling) && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4 flex items-center gap-3">
                <Loader2 className="w-4 h-4 animate-spin text-gray-500 flex-shrink-0" />
                <p className="text-gray-700 text-sm">AI review in progress...</p>
              </div>
            )}

            {/* Failed review banner with retry */}
            {review?.status === "failed" && !isPolling && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                <p className="text-red-700 text-sm font-medium mb-1">Review pipeline failed</p>
                {review.error_message && (
                  <p className="text-red-600 text-xs mb-3">{review.error_message}</p>
                )}
                {isAdmin && (
                  <button
                    type="button"
                    onClick={handleRetry}
                    disabled={isRetrying}
                    className="flex items-center gap-2 px-3 py-1.5 bg-red-700 text-white text-xs font-medium rounded hover:bg-red-800 transition-colors disabled:opacity-50"
                  >
                    {isRetrying ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                    {isRetrying ? "Retrying..." : "Retry Review"}
                  </button>
                )}
              </div>
            )}

            {/* Hide all review content when failed or polling */}
            {review?.status !== "failed" && !isPolling ? (
              <>
                {/* Success Banner */}
                {saveSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 flex items-center gap-2 mb-4">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                    <p className="text-green-700 text-sm font-medium">
                      Final review published successfully.
                    </p>
                  </div>
                )}

                {/* Relevance Warning */}
                {meta && !meta.is_relevant && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                    <p className="text-amber-800 text-sm">
                      <strong>Not Relevant</strong> ({meta.relevance_score}/100) —{" "}
                      {meta.relevance_explanation}
                    </p>
                  </div>
                )}

                {/* Total Score Box */}
                <div className="text-center bg-gray-50 border border-gray-200 rounded-lg py-5 mb-5">
                  <div className="text-xs text-gray-500 uppercase tracking-widest mb-1 font-medium">
                    {isAlreadyCommitted ? "Final Score" : "Your Score"}
                  </div>
                  <div className="text-5xl font-bold text-gray-900">{totalScore}</div>
                  <div className="text-gray-400 text-base">/100</div>
                </div>

                {/* Editable Sections */}
                <div className="space-y-3">
                  {SECTION_KEYS.map((key) => {
                    const section = watchedSections?.[key];
                    if (!section) return null;
                    const isExpanded = expandedSections.has(key);
                    const aiSection = aiSections[key];
                    const sectionMaxScore = section.max_score || 20;

                    return (
                      <div key={key} className="border border-gray-200 rounded-lg overflow-hidden">
                        {/* Section Header */}
                        <div className="flex items-center justify-between p-4 bg-gray-50">
                          <button
                            type="button"
                            onClick={() => toggleSection(key)}
                            className="flex items-center gap-2.5 text-left flex-1"
                          >
                            {isExpanded ? (
                              <ChevronDown className="w-4 h-4 text-gray-500" />
                            ) : (
                              <ChevronRight className="w-4 h-4 text-gray-500" />
                            )}
                            <span className="text-sm text-gray-900 font-semibold">
                              {formatSectionName(key)}
                            </span>
                          </button>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-1.5">
                              <FormField
                                control={control}
                                name={`sections.${key}.score`}
                                render={({ field }) => (
                                  <FormItem className="space-y-0">
                                    <FormControl>
                                      <Input
                                        type="number"
                                        min={0}
                                        max={sectionMaxScore}
                                        value={field.value}
                                        onChange={(e) =>
                                          updateScore(key, parseInt(e.target.value, 10) || 0)
                                        }
                                        onBlur={field.onBlur}
                                        name={field.name}
                                        ref={field.ref}
                                        disabled={!isEditable && !isAlreadyCommitted}
                                        className="w-16 h-auto bg-white border-gray-300 rounded-md px-2 py-1.5 text-center text-gray-900 font-bold text-base focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-primary"
                                      />
                                    </FormControl>
                                  </FormItem>
                                )}
                              />
                              <span className="text-gray-500 text-sm font-medium">
                                /{sectionMaxScore}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Section Body */}
                        {isExpanded && (
                          <div className="p-4 border-t border-gray-100 space-y-4">
                            {/* Scoring Guide */}
                            {SCORING_GUIDE[key] && (
                              <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                                <button
                                  type="button"
                                  onClick={() =>
                                    setShowGuide((prev) => ({ ...prev, [key]: !prev[key] }))
                                  }
                                  className="w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-100 transition-colors"
                                >
                                  <span className="text-xs text-gray-500 uppercase tracking-widest font-semibold">
                                    Scoring Guide
                                  </span>
                                  {showGuide[key] ? (
                                    <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                                  ) : (
                                    <ChevronRight className="w-3.5 h-3.5 text-gray-400" />
                                  )}
                                </button>
                                {showGuide[key] &&
                                  (() => {
                                    const guide = SCORING_GUIDE[key];
                                    return (
                                      <div className="px-3 pb-3 space-y-3 border-t border-gray-200">
                                        {/* Level Ranges */}
                                        <div className="pt-2.5">
                                          <h6 className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">
                                            Command Word Levels
                                          </h6>
                                          <div className="flex gap-1.5 flex-wrap">
                                            {guide.levels.map((level) => (
                                              <span
                                                key={level.name}
                                                className="text-xs px-2.5 py-1 rounded bg-white border border-gray-200 text-gray-600"
                                              >
                                                <span className="text-gray-800 font-semibold">
                                                  {level.name}
                                                </span>{" "}
                                                {level.range}
                                              </span>
                                            ))}
                                          </div>
                                        </div>
                                        {/* Score Interpretation */}
                                        <div>
                                          <h6 className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">
                                            Interpretation
                                          </h6>
                                          <div className="space-y-1.5">
                                            {guide.interpretation.map((item) => (
                                              <div
                                                key={item.range}
                                                className="flex gap-2.5 text-sm"
                                              >
                                                <span className="text-gray-600 font-mono w-12 flex-shrink-0 font-medium">
                                                  {item.range}
                                                </span>
                                                <span className="text-gray-600">
                                                  {item.description}
                                                </span>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                        {/* Escalation Caps */}
                                        <div>
                                          <h6 className="text-xs text-gray-500 uppercase tracking-wider mb-2 font-semibold">
                                            Escalation Caps
                                          </h6>
                                          <div className="space-y-1">
                                            {guide.caps.map((cap, i) => (
                                              <p key={i} className="text-sm text-amber-700">
                                                {cap}
                                              </p>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })()}
                              </div>
                            )}

                            {/* AI Decision */}
                            {aiSection && (
                              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
                                <h5 className="text-xs text-blue-700 uppercase tracking-widest font-semibold">
                                  AI Decision
                                </h5>
                                <div className="flex flex-wrap gap-3">
                                  <div className="text-sm">
                                    <span className="text-gray-500">Level: </span>
                                    <span className="font-semibold text-gray-900">
                                      {aiSection.command_word_level || aiSection.band || "—"}
                                    </span>
                                  </div>
                                  <div className="text-sm">
                                    <span className="text-gray-500">Score: </span>
                                    <span className="font-semibold text-gray-900">
                                      {aiSection.score}/{aiSection.max_score || 20}
                                    </span>
                                  </div>
                                </div>
                                {aiSection.cap_applied && aiSection.cap_reason && (
                                  <div className="bg-amber-50 border border-amber-200 rounded px-3 py-2 text-sm text-amber-800">
                                    <span className="font-semibold">Cap applied:</span>{" "}
                                    {aiSection.cap_reason}
                                  </div>
                                )}
                                {aiSection.layers && aiSection.layers.length > 0 && (
                                  <div className="space-y-2">
                                    {aiSection.layers.map((layer: any, li: number) => (
                                      <div key={li}>
                                        <div className="text-xs text-gray-500 font-semibold mb-1">
                                          {layer.name} ({layer.score}/{layer.max_points})
                                        </div>
                                        <div className="flex flex-wrap gap-1.5">
                                          {layer.components?.map((comp: any, ci: number) => (
                                            <span
                                              key={ci}
                                              className={`text-xs px-2 py-0.5 rounded border ${
                                                comp.score > 0
                                                  ? "bg-green-50 border-green-200 text-green-800"
                                                  : "bg-red-50 border-red-200 text-red-700"
                                              }`}
                                            >
                                              {comp.code}: {comp.score}/{comp.max_points}
                                            </span>
                                          ))}
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )}

                            {/* Diagnostic Explanation */}
                            <div>
                              <h5 className="text-sm font-semibold text-gray-700 mb-1.5">
                                Diagnostic
                              </h5>
                              <FormField
                                control={control}
                                name={`sections.${key}.diagnostic_explanation`}
                                render={({ field }) => (
                                  <FormItem className="space-y-0">
                                    <FormControl>
                                      <Textarea
                                        {...field}
                                        value={String(field.value ?? "")}
                                        disabled={!isEditable && !isAlreadyCommitted}
                                        rows={2}
                                        style={{ fieldSizing: "content" } as React.CSSProperties}
                                        className="w-full bg-white border-gray-200 rounded px-3 py-2 text-sm text-black focus:border-gray-400 focus:ring-0 resize-none overflow-hidden min-h-0"
                                        placeholder="Short diagnostic explanation of band placement..."
                                      />
                                    </FormControl>
                                    <FormMessage />
                                  </FormItem>
                                )}
                              />
                            </div>

                            {/* Observable Features + Missing Features */}
                            {(
                              [
                                "observable_features_detected",
                                "missing_features_for_next_band",
                              ] as const
                            ).map((field) => (
                              <SectionListField
                                key={field}
                                control={control}
                                sectionKey={key}
                                field={field}
                                disabled={!isEditable && !isAlreadyCommitted}
                                canEdit={isEditable || isAlreadyCommitted}
                              />
                            ))}

                            {/* Assessor Note — internal reasoning per criterion (KC-444) */}
                            {(isEditable || isAlreadyCommitted) && (
                              <div>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <h5 className="text-sm font-semibold text-gray-700">
                                    Assessor Note
                                  </h5>
                                  <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 border border-gray-200 text-gray-500 uppercase tracking-wide">
                                    Internal
                                  </span>
                                </div>
                                <FormField
                                  control={control}
                                  name={`sections.${key}.assessor_note`}
                                  render={({ field }) => (
                                    <FormItem className="space-y-0">
                                      <FormControl>
                                        <Textarea
                                          {...field}
                                          value={String(field.value ?? "")}
                                          disabled={!isEditable && !isAlreadyCommitted}
                                          rows={2}
                                          style={{ fieldSizing: "content" } as React.CSSProperties}
                                          className="w-full bg-gray-50 border-gray-200 rounded px-3 py-2 text-sm text-black focus:border-gray-400 focus:ring-0 resize-none overflow-hidden min-h-0"
                                          placeholder="How you reached this score — not shown to the participant..."
                                        />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                {review && <ApprovalHistory reviewId={review.id} />}
              </>
            ) : null}
          </div>

          {/* AI Feedback Panel — always visible when commit bar is shown */}
          {review?.status !== "failed" && !isPolling && (isEditable || isAlreadyCommitted) && (
            <div className="border-t border-gray-200 px-5 py-4 flex-shrink-0 bg-white space-y-3">
              <div className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
                AI Feedback
              </div>

              {/* Quality rating (required) */}
              <div>
                <div className="text-xs font-medium text-gray-700 mb-1.5">
                  Quality <span className="text-red-500">*</span>
                </div>
                <div className="flex gap-2">
                  {QUALITY_VALUES.map((q) => (
                    <button
                      key={q}
                      type="button"
                      onClick={() =>
                        form.setValue("adminFeedback.quality", q, { shouldDirty: true })
                      }
                      className={`px-3 py-1.5 rounded text-xs font-semibold border transition-colors ${
                        watchedQuality === q
                          ? q === "high"
                            ? "bg-green-600 border-green-600 text-white"
                            : q === "med"
                              ? "bg-amber-500 border-amber-500 text-white"
                              : "bg-red-500 border-red-500 text-white"
                          : "bg-white border-gray-300 text-gray-600 hover:border-gray-400"
                      }`}
                    >
                      {q.charAt(0).toUpperCase() + q.slice(1)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Flag checkboxes (optional) */}
              <FormField
                control={control}
                name="adminFeedback.flags"
                render={({ field }) => (
                  <FormItem>
                    <div className="text-xs font-medium text-gray-700 mb-1.5">Issues detected</div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1.5">
                      {FLAG_VALUES.map((flag) => {
                        const checked = (field.value as FlagValue[]).includes(flag);
                        return (
                          <label key={flag} className="flex items-center gap-1.5 cursor-pointer">
                            <Checkbox
                              checked={checked}
                              onCheckedChange={(c) => {
                                const next = c
                                  ? [...(field.value as FlagValue[]), flag]
                                  : (field.value as FlagValue[]).filter((f) => f !== flag);
                                field.onChange(next);
                              }}
                            />
                            <span className="text-xs text-gray-600">{FLAG_LABELS[flag]}</span>
                          </label>
                        );
                      })}
                    </div>
                  </FormItem>
                )}
              />

              {/* Note (optional) */}
              <FormField
                control={control}
                name="adminFeedback.note"
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Optional note (e.g. which criterion looked off, what the AI missed)"
                        rows={2}
                        className="w-full text-xs border-gray-200 rounded bg-white resize-none"
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          )}

          {/* Sticky Commit Bar — hidden for failed reviews and while polling */}
          {review?.status !== "failed" && !isPolling && (
            <div className="px-5 py-4 border-t border-gray-200 flex items-center justify-between flex-shrink-0 bg-gray-50">
              <div className="text-sm text-gray-600">
                Total: <span className="text-gray-900 font-bold text-base">{totalScore}/100</span>
              </div>
              {isEditable || isAlreadyCommitted ? (
                <button
                  type="submit"
                  disabled={isSaving || !watchedQuality}
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                  title={
                    !watchedQuality ? "Select an AI quality rating before committing" : undefined
                  }
                >
                  {isSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4" />
                  )}
                  {isAlreadyCommitted ? "Update Final Review" : "Commit Final Review"}
                </button>
              ) : (
                <span className="text-xs text-gray-400">Not submitted for review yet</span>
              )}
            </div>
          )}
        </form>
      </Form>
    );
  };

  // ================================================================
  // LOADING / ERROR STATES
  // ================================================================
  if (isLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!review || !submission) {
    const loadError =
      reviewQuery.isError || submissionQuery.isError
        ? reviewQuery.error instanceof Error
          ? reviewQuery.error.message
          : submissionQuery.error instanceof Error
            ? submissionQuery.error.message
            : "Failed to load review"
        : null;
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <p className="text-gray-500">{loadError ?? "Review not found"}</p>
        </div>
      </AdminLayout>
    );
  }

  // ================================================================
  // MAIN RENDER
  // ================================================================
  return (
    <AdminLayout>
      <div className="-m-4 md:-m-6 p-4 md:p-6 h-[calc(100vh-4rem)] overflow-hidden flex flex-col gap-3">
        {topic && (
          <div className="bg-white border border-gray-200 shadow-sm rounded-xl px-4 py-3 flex-shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-gray-400 mb-1">Topic</div>
            <div className="text-sm font-semibold text-gray-900">{topic.title}</div>
            {topic.description && (
              <p className="text-xs text-gray-500 mt-1 line-clamp-2">{topic.description}</p>
            )}
          </div>
        )}
        <div className="flex-1 min-h-0 overflow-hidden grid grid-cols-2 gap-5">
          {/* Left: PDF Viewer */}
          {renderPdfPanel()}

          {/* Right: Review Panel */}
          {isViewingOriginal ? renderReadOnlyPanel() : renderEditablePanel()}
        </div>
      </div>

      <Dialog open={promoteOpen} onOpenChange={setPromoteOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Promote review to fixture</DialogTitle>
            <DialogDescription>
              Copies this submission's article and topic into a new eval fixture. The review's AI
              and admin scores are recorded as ground truth in the fixture's notes.
            </DialogDescription>
          </DialogHeader>

          {promoteResult?.kind === "success" ? (
            <div className="text-sm text-green-700">
              ✓ Fixture created.{" "}
              <button
                type="button"
                className="underline font-medium"
                onClick={() =>
                  navigate(`/admin/assessment-fixtures/${promoteResult.fixtureId}/eval`)
                }
              >
                Open eval →
              </button>
            </div>
          ) : promoteResult?.kind === "duplicate" ? (
            <div className="text-sm text-amber-700">
              ⓘ Already promoted.{" "}
              <button
                type="button"
                className="underline font-medium"
                onClick={() =>
                  navigate(`/admin/assessment-fixtures/${promoteResult.fixtureId}/eval`)
                }
              >
                Open existing fixture →
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="block text-sm">
                <Label className="text-gray-700">Fixture name</Label>
                <Input
                  type="text"
                  value={promoteName}
                  onChange={(e) => setPromoteName(e.target.value)}
                  placeholder={defaultFixtureName}
                  className="mt-1 h-auto w-full rounded border-gray-300 px-2 py-1.5 text-sm"
                />
              </div>
              <div className="text-xs text-gray-500 space-y-0.5">
                <div>
                  Source review:{" "}
                  <span className="font-mono">{promotionTarget?.id?.slice(0, 8)}</span>{" "}
                  <span className="ml-1 inline-block rounded bg-gray-100 px-1.5 py-0.5 text-[10px] font-medium text-gray-700">
                    Rev {promotionRevision}
                    {hasHistory && promotionRevision === 2 ? " (current)" : ""}
                  </span>
                </div>
                <div>AI score: {promotionTarget?.final_score ?? "—"}/100</div>
                <div>Admin score: {promotionTarget?.admin_final_score ?? "—"}/100</div>
              </div>
              {promotionTarget?.admin_final_score === null ||
              promotionTarget?.admin_final_score === undefined ? (
                <div className="rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900 space-y-2">
                  <p>
                    <strong>No admin-confirmed score on this review.</strong> The fixture will be
                    created without a ground-truth score — evals run against it can compare model
                    output to the AI score only, not to a human grade. Promoting non-graded reviews
                    makes the eval corpus noisier and is logged for audit.
                  </p>
                  <label className="flex items-start gap-2">
                    <Checkbox
                      checked={promoteAckNoAdmin}
                      onCheckedChange={(c) => setPromoteAckNoAdmin(c === true)}
                      className="mt-0.5"
                    />
                    <span>I understand this fixture has no human-validated ground truth.</span>
                  </label>
                </div>
              ) : null}
              {promoteResult?.kind === "error" && (
                <div className="text-sm text-red-700">{promoteResult.message}</div>
              )}
            </div>
          )}

          <DialogFooter>
            {promoteResult?.kind === "success" || promoteResult?.kind === "duplicate" ? (
              <button
                type="button"
                onClick={() => setPromoteOpen(false)}
                className="px-4 py-2 text-sm rounded bg-gray-100 hover:bg-gray-200"
              >
                Close
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setPromoteOpen(false)}
                  disabled={promoting}
                  className="px-4 py-2 text-sm rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handlePromoteSubmit}
                  disabled={
                    promoting ||
                    ((promotionTarget?.admin_final_score === null ||
                      promotionTarget?.admin_final_score === undefined) &&
                      !promoteAckNoAdmin)
                  }
                  className="px-4 py-2 text-sm font-semibold rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
                >
                  {promoting && <Loader2 className="w-4 h-4 animate-spin" />}
                  Promote →
                </button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={rerunOpen} onOpenChange={setRerunOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Re-run AI review</DialogTitle>
            <DialogDescription>
              Re-queues the AI review pipeline for this submission. The current AI result
              {review?.admin_final_score !== null && review?.admin_final_score !== undefined
                ? " and the existing admin grading (score " +
                  review?.admin_final_score +
                  "/100) will be discarded"
                : " will be discarded"}{" "}
              and replaced by the new run.
            </DialogDescription>
          </DialogHeader>

          <div className="text-xs text-gray-500 space-y-0.5">
            <div>
              Review: <span className="font-mono">{review?.id?.slice(0, 8)}</span>
            </div>
            <div>
              Current status: <span className="font-medium">{review?.status ?? "—"}</span>
            </div>
          </div>
          {rerunError && <div className="text-sm text-red-700">{rerunError}</div>}

          <DialogFooter>
            <button
              type="button"
              onClick={() => setRerunOpen(false)}
              disabled={isRetrying}
              className="px-4 py-2 text-sm rounded bg-gray-100 hover:bg-gray-200 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleRerunConfirm}
              disabled={isRetrying}
              className="px-4 py-2 text-sm font-semibold rounded bg-primary text-white hover:bg-primary/90 disabled:opacity-50 inline-flex items-center gap-2"
            >
              {isRetrying ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              {isRetrying ? "Re-running..." : "Re-run AI review"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
